import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { config } from '@/lib/config';
import { type ProjectItem, type User } from '@/lib/types';

/** The drafting views a chapter can be presented in (#396). */
export type DisplayMode = 'verse' | 'pericope' | 'chapter';

interface ChapterViewAvailability {
  chapterAssignmentId: number;
  available: boolean;
  expectedVerseCount?: number;
  missingVerseNumbers?: number[] | null;
}

/** IDs are only meaningful within their project and source context; local DB resets can reuse them. */
export const isSameProjectAssignment = (
  left: ProjectItem | null | undefined,
  right: ProjectItem | null | undefined
): boolean =>
  !!left &&
  !!right &&
  left.chapterAssignmentId === right.chapterAssignmentId &&
  left.projectId === right.projectId &&
  left.projectUnitId === right.projectUnitId &&
  left.bibleId === right.bibleId &&
  left.bookId === right.bookId &&
  left.chapterNumber === right.chapterNumber;

interface AppState {
  userdetail: User | null;
  currentProjectItem: ProjectItem | null;
  presenceWarning: string | null;
  roleChangeWarning: boolean;
  _hasHydrated: boolean;
  displayMode: DisplayMode;
  chapterViewAvailability: ChapterViewAvailability | null;
  isAiThresholdMet: boolean | null;
  aiSyncPendingCount: number;
  manualAiEnable: { assignmentId: number; revision: number } | null;
  isOrgSwitching: boolean;
  aiAutoEnablePreferences: Record<number, boolean | undefined>;
  setUserDetail: (user: User) => void;
  setCurrentProjectItem: (projectItem: ProjectItem | null) => void;
  clearUserDetail: () => void;
  clearCurrentProjectItem: () => void;
  setHasHydrated: (state: boolean) => void;
  setPresenceWarning: (msg: string | null) => void;
  setRoleChangeWarning: (warning: boolean) => void;
  setDisplayMode: (mode: DisplayMode) => void;
  setChapterViewAvailability: (availability: ChapterViewAvailability | null) => void;
  setIsAiThresholdMet: (status: boolean | null) => void;
  beginAiSync: () => void;
  endAiSync: () => void;
  setAiEnabledFor: (
    assignmentId: number,
    value: boolean | undefined,
    manualEnable?: boolean
  ) => void;
  setIsOrgSwitching: (switching: boolean) => void;
  setAiAutoEnablePreference: (userId: number, status: boolean | undefined) => void;
}

/** Return only the live availability snapshot owned by this assignment. */
export function chapterViewAvailabilityFor(
  state: AppState,
  chapterAssignmentId: number | undefined
) {
  const availability = state.chapterViewAvailability;
  return availability?.chapterAssignmentId === chapterAssignmentId ? availability : null;
}

let hydrationResolve: (() => void) | null = null;
export const hydrationPromise = new Promise<void>(resolve => {
  hydrationResolve = resolve;
});

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      userdetail: null,
      currentProjectItem: null,
      presenceWarning: null,
      roleChangeWarning: false,
      _hasHydrated: false,
      displayMode: 'verse',
      chapterViewAvailability: null,
      isAiThresholdMet: null,
      aiSyncPendingCount: 0,
      manualAiEnable: null,
      isOrgSwitching: false,
      aiAutoEnablePreferences: {},
      setUserDetail: (userdetail: User) => set({ userdetail }),
      setCurrentProjectItem: (currentProjectItem: ProjectItem | null) => {
        if (!isSameProjectAssignment(get().currentProjectItem, currentProjectItem)) {
          // Clear assignment-scoped state when the selected context changes, even if IDs were reused.
          set({
            currentProjectItem,
            isAiThresholdMet: null,
            roleChangeWarning: false,
            chapterViewAvailability: null,
          });
        } else {
          // Keep assignment-scoped state when only its metadata changes.
          set({ currentProjectItem });
        }
      },
      clearUserDetail: () =>
        set({ userdetail: null, roleChangeWarning: false, chapterViewAvailability: null }),
      clearCurrentProjectItem: () =>
        set({
          currentProjectItem: null,
          isAiThresholdMet: null,
          roleChangeWarning: false,
          chapterViewAvailability: null,
        }),
      setHasHydrated: (state: boolean) => set({ _hasHydrated: state }),
      setPresenceWarning: (presenceWarning: string | null) => set({ presenceWarning }),
      setRoleChangeWarning: (roleChangeWarning: boolean) => set({ roleChangeWarning }),
      setDisplayMode: (displayMode: DisplayMode) => set({ displayMode }),
      setChapterViewAvailability: chapterViewAvailability => set({ chapterViewAvailability }),
      setIsAiThresholdMet: (status: boolean | null) => set({ isAiThresholdMet: status }),
      beginAiSync: () => set(state => ({ aiSyncPendingCount: state.aiSyncPendingCount + 1 })),
      endAiSync: () =>
        set(state => ({ aiSyncPendingCount: Math.max(0, state.aiSyncPendingCount - 1) })),
      setAiEnabledFor: (assignmentId, isAiEnabled, manualEnable = false) =>
        set(state => {
          if (state.currentProjectItem?.chapterAssignmentId !== assignmentId) return state;
          return {
            currentProjectItem: { ...state.currentProjectItem, isAiEnabled },
            ...(manualEnable && isAiEnabled
              ? {
                  manualAiEnable: {
                    assignmentId,
                    revision: (state.manualAiEnable?.revision ?? 0) + 1,
                  },
                }
              : {}),
          };
        }),
      setIsOrgSwitching: (isOrgSwitching: boolean) => set({ isOrgSwitching }),
      setAiAutoEnablePreference: (userId: number, status: boolean | undefined) =>
        set(state => {
          const next = { ...state.aiAutoEnablePreferences };
          if (status === undefined) {
            delete next[userId];
          } else {
            next[userId] = status;
          }
          return { aiAutoEnablePreferences: next };
        }),
    }),
    {
      name: 'app-store',
      partialize: state => ({
        userdetail: state.userdetail,
        currentProjectItem: state.currentProjectItem,
        displayMode: state.displayMode,
        aiAutoEnablePreferences: state.aiAutoEnablePreferences,
      }),
      onRehydrateStorage: () => state => {
        // The chapter surface is RTE-backed; a build without the flag must not revive a
        // stored 'chapter' selection into a mode the toggle cannot offer (#314).
        if (!config.features.rtePericope && state?.displayMode === 'chapter') {
          state.setDisplayMode('verse');
        }
        state?.setHasHydrated(true);
        if (hydrationResolve) {
          hydrationResolve();
          hydrationResolve = null;
        }
      },
    }
  )
);
